import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { paymentsAPI } from '../api/client'

interface Props {
  txId: number
  token: string
  counterpartyName?: string | null
  myRatingStars?: number | null
  canRate: boolean
  onDone: () => void
}

export default function DealRatingForm({
  txId,
  token,
  counterpartyName,
  myRatingStars,
  canRate,
  onDone,
}: Props) {
  const { t } = useTranslation()
  const [stars, setStars] = useState(0)
  const [hover, setHover] = useState(0)
  const [comment, setComment] = useState('')
  const [busy, setBusy] = useState(false)

  if (myRatingStars != null) {
    return (
      <div className="deal-rating deal-rating--done">
        <p className="deal-rating__label">{t('deals.rating.thanks')}</p>
        <div className="deal-rating__stars" aria-label={t('deals.rating.yourStars', { stars: myRatingStars })}>
          {[1, 2, 3, 4, 5].map((n) => (
            <span key={n} className={`deal-rating__star ${n <= myRatingStars ? 'deal-rating__star--on' : ''}`}>
              ★
            </span>
          ))}
        </div>
      </div>
    )
  }

  if (!canRate) return null

  function submit() {
    if (stars < 1) return
    setBusy(true)
    paymentsAPI
      .rateTransaction(txId, { stars, ...(comment.trim() ? { comment: comment.trim() } : {}) }, token)
      .then(() => onDone())
      .catch((err: { response?: { data?: { detail?: string } }; message?: string }) =>
        alert(err.response?.data?.detail || err.message),
      )
      .finally(() => setBusy(false))
  }

  return (
    <div className="deal-rating">
      <p className="deal-rating__label">
        {t('deals.rating.prompt', { name: counterpartyName || t('deals.rating.counterparty') })}
      </p>
      <div
        className="deal-rating__stars deal-rating__stars--pick"
        role="radiogroup"
        aria-label={t('deals.rating.aria')}
      >
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            className={`deal-rating__star-btn ${n <= (hover || stars) ? 'deal-rating__star-btn--on' : ''}`}
            onClick={() => setStars(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            disabled={busy}
            aria-label={`${n}/5`}
          >
            ★
          </button>
        ))}
      </div>
      <label className="deal-rating__comment-label">
        <span>{t('deals.rating.commentOptional')}</span>
        <textarea
          className="deal-rating__comment"
          rows={2}
          maxLength={500}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t('deals.rating.commentPlaceholder')}
        />
      </label>
      <button type="button" className="btn-deal-action btn-deal-rate" disabled={busy || stars < 1} onClick={submit}>
        {busy ? '…' : t('deals.rating.submit')}
      </button>
    </div>
  )
}
